import _ from 'lodash';
import moment from 'moment';
import { showStatus } from '@/plugins/vue-notification';

// Labels of the API's lead time buckets (how far ahead of a day something happened), in order.
export const LEAD_TIME_LABELS = {
  after: 'leadTimeAfter',
  same_day: 'leadTimeSameDay',
  days_1_3: 'leadTime1To3',
  days_4_7: 'leadTime4To7',
  days_8_14: 'leadTime8To14',
  days_15_30: 'leadTime15To30',
  days_31_plus: 'leadTime31Plus'
};

/**
 * Shared behavior of the report dashboards. A dashboard sets `retrieveAction` (the store action that loads its
 * numbers) and `retrieveFailMessage` (the description shown when that fails), and passes the criteria emitted by
 * `DashboardFilters` to `setCriteria`. The loaded numbers end up in `dashboard`.
 */
export default {
  props: {
    contentHeight: {
      default: '88vh',
      type: String
    }
  },
  data () {
    return {
      chartStyles: {
        height: '240px',
        position: 'relative'
      },
      criteria: null,
      dashboard: null,
      loading: false,
      requestCount: 0,
      requestedKey: null
    };
  },
  computed: {
    colors () {
      return this.$vuetify.theme.currentTheme;
    },
    doughnutOptions () {
      return {
        legend: {
          labels: {
            boxWidth: 12
          },
          position: 'bottom'
        },
        maintainAspectRatio: false,
        responsive: true
      };
    },
    groupedBarOptions () {
      return {
        ...this.stackedBarOptions,
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      };
    },
    horizontalStackedBarOptions () {
      return {
        ...this.stackedBarOptions,
        scales: {
          xAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
          yAxes: [{ gridLines: { display: false }, stacked: true }]
        }
      };
    },
    isDepartmentManagement () {
      return this.$store.getters['account/isDepartmentManagement']();
    },
    stackedBarOptions () {
      return {
        legend: {
          labels: {
            boxWidth: 12
          },
          position: 'bottom'
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{ gridLines: { display: false }, stacked: true }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }]
        },
        tooltips: {
          intersect: false,
          mode: 'index'
        }
      };
    }
  },
  created () {
    this.debouncedLoad = _.debounce(this.load, 300);
  },
  beforeDestroy () {
    this.debouncedLoad.cancel();
  },
  methods: {
    // Rows of a horizontal bar chart need room for their labels.
    barChartStyles (rowCount) {
      return {
        ...this.chartStyles,
        height: `${Math.max(240, rowCount * 32)}px`
      };
    },
    dataset (label, color, data) {
      return {
        backgroundColor: color,
        data,
        label
      };
    },
    dayLabels (days) {
      const format = this.$store.getters['org/getDateFormatShort']();
      return days.map(day => moment(day.date).format(format));
    },
    // This function is added mainly for easy of mocking during in unit tests.
    dispatch (action, payload) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch(action, payload).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    },
    doughnutData (rows, valueKey) {
      const palette = [
        this.colors.secondary, this.colors.accent, this.colors.primary, this.colors.info,
        this.colors.success, this.colors.warning, this.colors.error, '#9E9E9E'
      ];
      return {
        labels: rows.map(row => row.name || this.$t('labels.unknown')),
        datasets: [{
          backgroundColor: rows.map((row, index) => palette[index % palette.length]),
          data: rows.map(row => row[valueKey])
        }]
      };
    },
    // Hours as "18 h", or as days ("2.5 d") from 48 hours.
    formatHours (hours) {
      if (hours === null || hours === undefined) {
        return '-';
      }
      if (hours < 48) {
        return this.$t('labels.hoursShort', { hours: this.formatNumber(hours) });
      }
      return this.$t('labels.daysShort', { days: this.formatNumber(Math.round(hours / 24 * 10) / 10) });
    },
    formatNumber (value) {
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
    },
    formatPercent (value) {
      return value === null || value === undefined ? '-' : `${this.formatNumber(value)}%`;
    },
    load () {
      if (!this.criteria) {
        return;
      }
      // Filters can change and change back before the debounced load runs.
      const key = JSON.stringify(this.criteria);
      if (key === this.requestedKey) {
        return;
      }
      this.requestedKey = key;
      // Only the latest request updates the dashboard.
      const request = ++this.requestCount;
      this.loading = true;
      this.dispatch(this.retrieveAction, this.criteria).then((dashboard) => {
        if (request === this.requestCount) {
          this.dashboard = dashboard;
        }
      }).catch((error) => {
        if (request === this.requestCount) {
          // Let the same filters be tried again.
          this.requestedKey = null;
          showStatus({
            text: this.$t(this.retrieveFailMessage),
            type: 'error',
            data: {
              error: _.get(error, 'response.data')
            }
          });
        }
      }).finally(() => {
        if (request === this.requestCount) {
          this.loading = false;
        }
      });
    },
    // The days of the week from the hospital's first day, as moment day numbers (0 is Sunday).
    weekdayOrder () {
      const startOfWeek = _.get(this.$store.state.org, ['settings', 'scheduling', 'startOfWeek'], 'Sunday');
      const firstDay = moment().day(startOfWeek).day();
      return _.range(7).map(offset => (firstDay + offset) % 7);
    },
    // The dashboards' APIs number weekdays from Monday (0); moment from Sunday (0).
    weekdayRows (rows) {
      const rowsByWeekday = _.keyBy(rows, 'weekday');
      return this.weekdayOrder().map(day => ({
        ...rowsByWeekday[(day + 6) % 7],
        name: moment().day(day).format('ddd')
      }));
    },
    setCriteria (criteria) {
      // The first criteria load right away; later changes wait for the user to settle.
      const first = this.criteria === null;
      this.criteria = criteria;
      if (first) {
        this.load();
      } else {
        this.debouncedLoad();
      }
    }
  }
};
